import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Hourglass, SignOut } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { pendingRegistrationStore } from '../lib/api';

/**
 * Pending-approval landing page. Stable across the sign-out boundary:
 * the greeted first name and copy are identical whether the user is
 * authenticated or has just signed out. We persist the registrant's
 * first name in localStorage when they submit the join form so the
 * page never falls back to "there" (or breaks the copy) after logout.
 */
export default function RegistrationPendingPage() {
  const navigate = useNavigate();
  const { user, accountStatus, logout } = useAuth();

  // Read once on mount; even if React state changes later, this snapshot
  // keeps the render stable through the sign-out re-render cycle.
  const [cachedFirstName] = useState(() => pendingRegistrationStore.get()?.first_name || '');

  const firstName =
    user?.first_name ||
    cachedFirstName ||
    'there';

  // If an APPROVED user somehow lands here (e.g. they bookmarked the URL),
  // bounce them to the home route so they're not staring at a stale
  // pending screen. Pending and denied users stay on this page.
  useEffect(() => {
    if (user && accountStatus && accountStatus !== 'pending_approval' && accountStatus !== 'denied') {
      navigate('/', { replace: true });
    }
  }, [user, accountStatus, navigate]);

  return (
    <div className="min-h-screen bg-[#fbfaf8] flex flex-col items-center justify-center px-6 py-10">
      <div
        data-testid="registration-pending-page"
        className="w-full max-w-md bg-white rounded-3xl border border-[#e3dcd1] shadow-[0_20px_60px_rgba(1,53,35,0.08)] p-8 text-center space-y-5"
      >
        <div className="w-16 h-16 mx-auto rounded-full bg-[#c2a251]/15 flex items-center justify-center">
          <Hourglass size={32} weight="duotone" className="text-[#c2a251]" />
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#015e3f] font-semibold">
            Pending Approval
          </p>
          <h1 className="mt-1 font-serif text-3xl sm:text-4xl text-[#013523] tracking-tight">
            Thanks, {firstName}!
          </h1>
        </div>

        <p className="text-sm text-[#2a332c] leading-relaxed">
          Your registration has been received. The Rippers Club admin will
          review your request shortly. We'll send you an email as soon as your
          account is approved.
        </p>

        <p className="text-xs text-[#6a766e] leading-relaxed">
          You can close this page — once approved you'll be able to sign in
          and start challenging other members of the ladder.
        </p>

        {/* Show the Sign Out control only while the user is authenticated.
            After sign-out the same screen re-renders without it, but the
            heading + body copy stay identical. */}
        {user ? (
          <button
            data-testid="pending-logout"
            onClick={logout}
            className="inline-flex items-center justify-center gap-2 text-sm text-[#6a766e] hover:text-[#013523] mt-2"
          >
            <SignOut size={16} />
            Sign out
          </button>
        ) : (
          <Link
            to="/login"
            data-testid="pending-back-to-signin"
            className="inline-flex items-center justify-center gap-2 text-sm text-[#6a766e] hover:text-[#013523] mt-2"
          >
            Back to sign in
          </Link>
        )}
      </div>

      <div className="mt-6 flex items-center gap-3 text-[11px] text-[#6a766e]">
        <Link to="/privacy" className="hover:text-[#015e3f]">Privacy</Link>
        <span aria-hidden="true" className="text-[#e3dcd1]">·</span>
        <Link to="/terms" className="hover:text-[#015e3f]">Terms</Link>
        <span aria-hidden="true" className="text-[#e3dcd1]">·</span>
        <Link to="/sms-terms" className="hover:text-[#015e3f]">SMS Terms</Link>
      </div>
    </div>
  );
}
